import { useState } from 'react';

import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { Contact } from '@repo/shared-types';

const CONFIRM_WORD = 'DELETE';

/**
 * Typed-confirmation purge dialog: removes the contact, every call, and every
 * chat from the system. Rendered inside DialogContent (unmounts on close), so
 * the typed text resets on every open.
 */
const DeleteContactForm = ({
  contact,
  isDeleting,
  onConfirm,
  onClose,
}: {
  contact: Contact;
  isDeleting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim() === CONFIRM_WORD;

  return (
    <>
      <div className="space-y-3">
        <div className="bg-destructive/10 text-destructive flex items-start gap-2.5 rounded-lg border border-red-500/20 px-3 py-2.5 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">This permanently removes everything about this contact:</p>
            <ul className="mt-1 list-inside list-disc text-xs">
              <li>Contact details, identifiers, and phone numbers</li>
              <li>All calls, transcripts, and reviews</li>
              <li>All chats and messages (WhatsApp, email, web widget)</li>
            </ul>
            <p className="mt-1 text-xs">This cannot be undone.</p>
          </div>
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-sm">
            Type <span className="text-foreground font-mono font-semibold">{CONFIRM_WORD}</span> to
            confirm deleting <span className="text-foreground font-medium">{contact.name}</span>
          </label>
          <input
            className="focus:ring-destructive/50 w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={CONFIRM_WORD}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={onConfirm} disabled={!confirmed || isDeleting}>
          {isDeleting ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="mr-1.5 h-4 w-4" />
          )}
          Delete everything
        </Button>
      </DialogFooter>
    </>
  );
};

export const DeleteContactDialog = ({
  contact,
  open,
  isDeleting,
  onOpenChange,
  onConfirm,
}: {
  contact: Contact;
  open: boolean;
  isDeleting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) => (
  <Dialog open={open} onOpenChange={(next) => (isDeleting ? undefined : onOpenChange(next))}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Delete contact</DialogTitle>
        <DialogDescription>
          Remove {contact.name} and their entire history from the system.
        </DialogDescription>
      </DialogHeader>
      <DeleteContactForm
        contact={contact}
        isDeleting={isDeleting}
        onConfirm={onConfirm}
        onClose={() => onOpenChange(false)}
      />
    </DialogContent>
  </Dialog>
);
